import { isTrellisTheme } from 'foundations-theming/utils';
/** @returns true if the button looks like a `<button>` */
export const isButtonUse = use => use === 'primary' || use === 'primary-white' || use === 'primary-on-fill' || use === 'secondary' || use === 'secondary-ghost' || use === 'secondary-alt' || use === 'tertiary' || use === 'tertiary-light' || use === 'tertiary-extra-light' || use === 'tertiary-on-fill' || use === 'tertiary-transparent' || use === 'danger' || use === 'transparent' || use === 'view-group';

/** @returns true if the button looks like a form `<input>` */
export const isFormUse = use => use === 'form' || use === 'form-on-dark';

/** @returns true if the button looks like a link `<a>` */
export const isLinkUse = use => use === 'link' || use === 'link-danger' || use === 'link-on-dark' || use === 'link-on-bright' || use === 'link-on-fill' || use === 'control' || use === 'control-on-bright' || use === 'control-on-dark' || use === 'control-danger' || use === 'control-on-fill';
export const isControlUse = use => use === 'control' || use === 'control-on-bright' || use === 'control-on-dark' || use === 'control-danger' || use === 'control-on-fill';

/** @returns true if the button has use `transparent` */
export const isTransparentUse = use => use === 'transparent';

/** @returns true if the button looks like an unstyled `<div>` */
export const isUnstyledUse = use => use === 'unstyled';

/**
 * These styles are used by the following components and styled-components wrappers:
 *
 * - PrivateButton: StyledButtonTextContainer
 * - LoadingButton: StyledLoadingButtonContent
 *
 * Make sure to read the PR below if you consider changing the display property.
 *
 * @see https://git.hubteam.com/HubSpot/foundations-components/pull/6634
 */
export const getSharedButtonContainerStyles = (theme, use) => ({
  display: 'flex',
  // link variant Buttons should behave like the Link component, which does not provide default spacing for children
  gap: isLinkUse(use) ? theme.space['0'] : theme.space['200'],
  alignItems: 'center',
  inlineSize: '100%',
  blockSize: '100%'
});
const sharedDisabledTokens = {
  backgroundColor: 'fill-primary-disabled',
  borderColor: 'border-primary-disabled',
  color: 'text-primary-disabled',
  cursor: 'auto'
};
const tertiaryLightStyles = {
  idle: {
    backgroundColor: 'fill-tertiary-default',
    borderColor: 'border-tertiary-default',
    color: 'text-tertiary-default'
  },
  hover: {
    backgroundColor: 'fill-tertiary-hover',
    borderColor: 'border-tertiary-default',
    color: 'text-tertiary-default',
    textDecoration: 'none'
  },
  active: {
    backgroundColor: 'fill-tertiary-pressed',
    borderColor: 'border-tertiary-default',
    color: 'text-tertiary-default'
  },
  disabled: {
    backgroundColor: 'fill-tertiary-disabled',
    borderColor: 'border-tertiary-disabled',
    color: 'text-tertiary-disabled',
    cursor: 'auto'
  }
};
export const setAsActive = useTokens => {
  useTokens.idle = useTokens.active;
  return useTokens;
};
export const getUseTokens = use => {
  const tokens = {
    primary: {
      idle: {
        backgroundColor: 'fill-primary-default',
        borderColor: 'border-primary-default',
        color: 'text-primary-default'
      },
      hover: {
        backgroundColor: 'fill-primary-hover',
        borderColor: 'border-primary-default',
        color: 'text-primary-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-primary-pressed',
        borderColor: 'border-primary-default',
        color: 'text-primary-default'
      },
      disabled: Object.assign({}, sharedDisabledTokens)
    },
    'primary-white': {
      idle: {
        backgroundColor: 'fill-inverse-default-alt',
        borderColor: 'border-inverse-default-alt',
        color: 'text-inverse-default-alt'
      },
      hover: {
        backgroundColor: 'fill-inverse-hover-alt',
        borderColor: 'border-inverse-default-alt',
        color: 'text-inverse-default-alt'
      },
      active: {
        backgroundColor: 'fill-inverse-pressed-alt',
        borderColor: 'border-inverse-default-alt',
        color: 'text-inverse-default-alt'
      },
      disabled: {
        backgroundColor: 'fill-inverse-disabled-alt',
        borderColor: 'border-inverse-disabled-alt',
        color: 'text-inverse-disabled-alt',
        cursor: 'auto'
      }
    },
    'primary-on-fill': {
      idle: {
        backgroundColor: 'specialty-primary-on-fill-default',
        borderColor: 'border-primary-default',
        color: 'text-inverse-default'
      },
      hover: {
        backgroundColor: 'specialty-primary-on-fill-hover',
        borderColor: 'border-primary-default',
        color: 'text-inverse-default'
      },
      active: {
        backgroundColor: 'specialty-primary-on-fill-pressed',
        borderColor: 'border-primary-default',
        color: 'text-inverse-default'
      },
      disabled: {
        backgroundColor: 'specialty-primary-on-fill-disabled',
        borderColor: 'border-primary-disabled',
        color: 'text-core-disabled'
      }
    },
    secondary: {
      idle: {
        backgroundColor: 'fill-secondary-default',
        borderColor: 'border-secondary-default',
        color: 'text-secondary-default'
      },
      hover: {
        backgroundColor: 'fill-secondary-hover',
        borderColor: 'border-secondary-default',
        color: 'text-secondary-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-secondary-pressed',
        borderColor: 'border-secondary-default',
        color: 'text-secondary-default'
      },
      disabled: {
        backgroundColor: 'fill-secondary-disabled',
        borderColor: 'border-secondary-disabled',
        color: 'text-secondary-disabled',
        cursor: 'auto'
      }
    },
    'secondary-ghost': {
      idle: {
        backgroundColor: 'fill-inverse-default',
        borderColor: 'border-inverse-default',
        color: 'text-inverse-default'
      },
      hover: {
        backgroundColor: 'fill-inverse-hover',
        borderColor: 'border-inverse-default',
        color: 'text-inverse-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-inverse-pressed',
        borderColor: 'border-inverse-default',
        color: 'text-inverse-default'
      },
      disabled: {
        backgroundColor: 'fill-inverse-disabled',
        borderColor: 'border-inverse-disabled',
        color: 'text-core-disabled',
        cursor: 'auto'
      }
    },
    'secondary-alt': {
      idle: {
        backgroundColor: 'fill-secondary-default-alt',
        borderColor: 'border-secondary-default-alt',
        color: 'text-secondary-default'
      },
      hover: {
        backgroundColor: 'fill-secondary-hover-alt',
        borderColor: 'border-secondary-default-alt',
        color: 'text-secondary-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-secondary-pressed-alt',
        borderColor: 'border-secondary-default-alt',
        color: 'text-secondary-default'
      },
      disabled: {
        backgroundColor: 'fill-secondary-disabled-alt',
        // TODO: get secondary disabled alt border
        borderColor: 'border-secondary-disabled',
        color: 'text-secondary-disabled',
        cursor: 'auto'
      }
    },
    tertiary: {
      idle: {
        backgroundColor: 'fill-transitional-tertiary-default',
        borderColor: 'border-transitional-tertiary-default',
        color: 'text-inverse-default'
      },
      hover: {
        backgroundColor: 'fill-transitional-tertiary-hover',
        borderColor: 'border-transitional-tertiary-default',
        color: 'text-inverse-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-transitional-tertiary-pressed',
        borderColor: 'border-transitional-tertiary-default',
        color: 'text-inverse-default'
      },
      disabled: {
        backgroundColor: 'fill-tertiary-disabled',
        borderColor: 'border-tertiary-disabled',
        color: 'text-tertiary-disabled',
        cursor: 'auto'
      }
    },
    'tertiary-light': Object.assign({}, tertiaryLightStyles),
    'tertiary-extra-light': {
      idle: {
        backgroundColor: 'fill-field-default-alt',
        borderColor: 'border-tertiary-default',
        color: 'text-tertiary-default'
      },
      hover: {
        backgroundColor: 'fill-tertiary-hover',
        borderColor: 'border-tertiary-default',
        color: 'text-tertiary-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-tertiary-pressed',
        borderColor: 'border-tertiary-default',
        color: 'text-tertiary-default'
      },
      disabled: {
        backgroundColor: 'fill-tertiary-disabled',
        borderColor: 'border-tertiary-disabled',
        color: 'text-tertiary-disabled',
        cursor: 'auto'
      }
    },
    'tertiary-transparent': {
      idle: {
        backgroundColor: 'specialty-tertiary-transparent-default',
        borderColor: 'border-tertiary-default',
        color: 'text-core-default'
      },
      hover: {
        backgroundColor: 'specialty-tertiary-transparent-hover',
        borderColor: 'border-tertiary-default',
        color: 'text-core-default'
      },
      active: {
        backgroundColor: 'specialty-tertiary-transparent-pressed',
        borderColor: 'border-tertiary-default',
        color: 'text-core-default'
      },
      disabled: {
        backgroundColor: 'specialty-tertiary-transparent-disabled',
        borderColor: 'border-tertiary-disabled',
        color: 'text-tertiary-disabled',
        cursor: 'auto'
      }
    },
    'tertiary-on-fill': {
      idle: {
        backgroundColor: 'specialty-tertiary-on-fill-default',
        borderColor: 'border-tertiary-default',
        color: 'text-inverse-default-alt'
      },
      hover: {
        backgroundColor: 'specialty-tertiary-on-fill-hover',
        borderColor: 'border-tertiary-default',
        color: 'text-inverse-default-alt',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'specialty-tertiary-on-fill-pressed',
        borderColor: 'border-tertiary-default',
        color: 'text-inverse-default-alt'
      },
      disabled: {
        backgroundColor: 'specialty-tertiary-on-fill-disabled',
        borderColor: 'border-core-hover',
        color: 'text-tertiary-disabled',
        cursor: 'auto'
      }
    },
    danger: {
      idle: {
        backgroundColor: 'fill-alert-default',
        borderColor: 'fill-alert-default',
        color: 'text-alert-on-fill-default'
      },
      hover: {
        backgroundColor: 'fill-alert-hover',
        borderColor: 'fill-alert-hover',
        color: 'text-alert-on-fill-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-alert-pressed',
        borderColor: 'fill-alert-pressed',
        color: 'text-alert-on-fill-default'
      },
      disabled: Object.assign({}, sharedDisabledTokens)
    },
    transparent: {
      idle: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-text-interactive-alt-default'
      },
      hover: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-text-interactive-alt-hover'
      },
      active: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-text-interactive-alt-pressed'
      },
      disabled: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default-disabled'
      }
    },
    unstyled: {
      idle: {
        backgroundColor: 'fill-surface-default',
        borderColor: 'border-core-on-fill-default',
        color: 'text-core-default'
      },
      hover: {
        backgroundColor: 'fill-surface-default',
        borderColor: 'border-core-on-fill-default',
        color: 'text-core-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-surface-default',
        borderColor: 'border-core-on-fill-default',
        color: 'text-core-default'
      },
      disabled: Object.assign({}, sharedDisabledTokens)
    },
    form: {
      idle: {
        backgroundColor: 'fill-surface-raised',
        borderColor: 'border-core-default',
        color: 'text-core-default'
      },
      hover: {
        backgroundColor: 'fill-surface-raised',
        borderColor: 'border-core-default',
        color: 'text-core-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-surface-raised',
        borderColor: 'border-core-default',
        color: 'text-core-default'
      },
      disabled: Object.assign({}, sharedDisabledTokens)
    },
    'form-on-dark': {
      idle: {
        backgroundColor: 'fill-surface-default',
        borderColor: 'border-core-default',
        color: 'text-core-default'
      },
      hover: {
        backgroundColor: 'fill-surface-default',
        borderColor: 'border-core-default',
        color: 'text-core-default',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-surface-default',
        borderColor: 'border-core-default',
        color: 'text-core-default'
      },
      disabled: Object.assign({}, sharedDisabledTokens)
    },
    link: {
      idle: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default'
      },
      hover: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default-hover'
      },
      active: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default-pressed'
      },
      disabled: Object.assign({}, sharedDisabledTokens, {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default-disabled'
      })
    },
    'link-danger': {
      idle: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-alert-default'
      },
      hover: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-alert-hover'
      },
      active: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-alert-pressed'
      },
      disabled: Object.assign({}, sharedDisabledTokens, {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default-disabled'
      })
    },
    'link-on-dark': {
      idle: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-link-on-fill-alt-default'
      },
      hover: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default-hover'
      },
      active: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-link-on-fill-alt-pressed'
      },
      disabled: Object.assign({}, sharedDisabledTokens, {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-inverse-disabled'
      })
    },
    'link-on-bright': {
      idle: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-text-core-alt-default'
      },
      hover: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default-hover'
      },
      active: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default-pressed'
      },
      disabled: Object.assign({}, sharedDisabledTokens, {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default-disabled'
      })
    },
    'link-on-fill': {
      idle: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-link-on-fill-default'
      },
      hover: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-link-on-fill-hover'
      },
      active: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-link-on-fill-pressed'
      },
      disabled: Object.assign({}, sharedDisabledTokens, {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-link-on-fill-disabled'
      })
    },
    'view-group': Object.assign({}, tertiaryLightStyles),
    control: {
      idle: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-text-interactive-alt-default',
        textDecoration: 'none'
      },
      hover: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-text-interactive-alt-hover',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-text-interactive-alt-pressed',
        textDecoration: 'none'
      },
      disabled: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default-disabled',
        textDecoration: 'none'
      }
    },
    'control-on-dark': {
      idle: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-inverse-default',
        textDecoration: 'none'
      },
      hover: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-inverse-hover',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-inverse-pressed',
        textDecoration: 'none'
      },
      disabled: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-inverse-disabled',
        textDecoration: 'none'
      }
    },
    'control-on-bright': {
      idle: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-core-default',
        textDecoration: 'none'
      },
      hover: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-text-interactive-alt-hover',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-text-interactive-alt-pressed',
        textDecoration: 'none'
      },
      disabled: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-core-disabled',
        textDecoration: 'none'
      }
    },
    'control-danger': {
      idle: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-alert-default',
        textDecoration: 'none'
      },
      hover: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-alert-hover',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-alert-pressed',
        textDecoration: 'none'
      },
      disabled: Object.assign({}, sharedDisabledTokens, {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'text-interactive-default-disabled',
        textDecoration: 'none'
      })
    },
    'control-on-fill': {
      idle: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-link-on-fill-default',
        textDecoration: 'none'
      },
      hover: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-link-on-fill-hover',
        textDecoration: 'none'
      },
      active: {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-link-on-fill-pressed',
        textDecoration: 'none'
      },
      disabled: Object.assign({}, sharedDisabledTokens, {
        backgroundColor: 'fill-transparent-default',
        borderColor: 'fill-transparent-default',
        color: 'specialty-link-on-fill-disabled',
        textDecoration: 'none'
      })
    }
  };
  return tokens[use];
};
export const shouldButtonBeUnderlined = (use, theme) => isTrellisTheme(theme) && isLinkUse(use) && !isControlUse(use);

/**
 * This helper retrieves size tokens for a particular size (extra-small, small, medium).
 *
 * @param size - The current variant of the Button.
 * @param use - The current variant of the Button.
 *
 * @returns A new object containing the typography, padding, and gap tokens for a particular size.
 */
export const getSizeTokens = (size, use, floatingGroup) => {
  /**
   * Tokens for common properties across variants
   *
   * iconSize follows the same sizing increments as the ones that were applied separately to ExternalLinkIcon.
   * This ensures the same height between PrivateButtonLinks with non-ExternalLinkIcon icons (using hideExternalLinkIcon prop)
   * and PrivateButtonLinks with non-ExternalLinkIcon icons AND ExternalLinkIcon.
   */
  const tokens = {
    medium: {
      gap: '200',
      paddingBlockStart: '250',
      paddingBlockEnd: '250',
      iconSize: '200',
      typography: 'heading-50',
      paddingInlineStart: floatingGroup ? '400' : '600',
      paddingInlineEnd: floatingGroup ? '400' : '600'
    },
    small: {
      gap: '200',
      paddingBlockStart: '200',
      paddingBlockEnd: '200',
      iconSize: '100',
      typography: 'detail-100',
      paddingInlineStart: '400',
      paddingInlineEnd: '400'
    },
    'extra-small': {
      gap: '100',
      paddingBlockStart: '100',
      paddingBlockEnd: '100',
      iconSize: '100',
      typography: 'detail-100',
      paddingInlineStart: floatingGroup ? '400' : '300',
      paddingInlineEnd: floatingGroup ? '400' : '300'
    }
  };

  // Specific adjustments for 'transparent' use
  const transparentAdjustments = {
    medium: {
      paddingInlineStart: '300',
      paddingInlineEnd: '300'
    },
    small: {
      paddingInlineStart: '200',
      paddingInlineEnd: '200'
    },
    'extra-small': {
      paddingInlineStart: '100',
      paddingInlineEnd: '100'
    }
  };

  // Specific adjustments for 'form' use
  const formAdjustments = {
    medium: {
      typography: 'body-200',
      paddingInlineStart: '300',
      paddingInlineEnd: '400'
    },
    small: {
      typography: 'detail-200',
      paddingInlineStart: '200',
      paddingInlineEnd: '300'
    },
    'extra-small': {
      typography: 'detail-200',
      paddingInlineStart: '100',
      paddingInlineEnd: '200'
    }
  };
  if (use === 'transparent') {
    tokens[size] = Object.assign({}, tokens[size], transparentAdjustments[size]);
  }
  if (isFormUse(use)) {
    tokens[size] = Object.assign({}, tokens[size], formAdjustments[size], {
      width: '100%'
    });
  }
  if (isLinkUse(use)) {
    tokens[size] = Object.assign({}, tokens[size], {
      paddingBlockStart: '0',
      paddingInlineEnd: '0',
      paddingBlockEnd: '0',
      paddingInlineStart: '0'
    });
  }
  return tokens[size];
};
const LINK_LIKE_USES = ['transparent', 'unstyled'];
export const isLinkLikeUse = use => isLinkUse(use) || LINK_LIKE_USES.includes(use);

/**
 * Computes the final button use based on the provided use and toolbar context.
 */
export const getComputedUse = ({
  use,
  isInFilledToolbar,
  isTrellis
}) => {
  if (isInFilledToolbar) {
    // we always want to use link-on-fill or control-on-fill in a toolbar unless it's *-danger
    // because we're no longer clobbering link styles in toolbars, this needs to work across themes
    if (isLinkUse(use) && !use.includes('danger')) {
      return isControlUse(use) ? 'control-on-fill' : 'link-on-fill';
    }

    // we want new secondary-alt buttons to render as secondary-ghost in canvas
    // secondary-alt (lavender) buttons are inverted in Canvas (orange on white) and look bad against dark toolbar fills
    if (!isTrellis) {
      if (use === 'secondary-alt') {
        return 'secondary-ghost';
      }
      return use;
    }

    // Custom mapping for buttons in a toolbar only in the Trellis theme
    // These mappings are defined by UX.
    // This is a temporary solution to avoid a `Button` `use` codemod/migration for buttons used in a toolbar.
    if (isTrellis) {
      switch (use) {
        case 'primary':
          return 'primary-on-fill';
        case 'secondary':
          return 'secondary-alt';
        case 'secondary-ghost':
          return 'tertiary-on-fill';
        case 'tertiary':
          return 'tertiary-on-fill';
        default:
          return use;
      }
    }
  }
  return use;
};
export const getControlMapping = use => {
  switch (use) {
    case 'link':
      return 'control';
    case 'link-on-bright':
      return 'control-on-bright';
    case 'link-on-dark':
      return 'control-on-dark';
    case 'link-danger':
      return 'control-danger';
    case 'link-on-fill':
      return 'control-on-fill';
    default:
      return use;
  }
};